import { Product } from '../../../api/types'

type Features = Record<string, boolean>

enum PlanType {
  STARTER = 'starter',
  PROFESSIONAL = 'professional',
}

enum Interval {
  MONTH = 'month',
  YEAR = 'year',
}

enum ProductType {
  USAGE = 'usage',
}

enum UsageType {
  FILE = 'file',
  PDV = 'PDV',
}

const filterProductsByMetadata = (products: Product[], productType: ProductType, usageType: UsageType): Product[] => {
  return products.filter(
    (product) => product.metadata.productType === productType && product.metadata.usageType === usageType
  )
}

const findDefaultProduct = (products: Product[]): Product | undefined => {
  return products.find((product) => product.metadata.isDefaultSelection === 'true')
}

const findProductByMetadata = (products: Product[], planType: PlanType, interval: Interval | null = null) => {
  return products.find(
    (product) => product.metadata.planType === planType && (!interval || product.metadata.interval === interval)
  )
}

interface IPlan {
  product: Product
  lead?: string
  subhead?: string
  featuresList?: {
    title: string
    sections: {
      title?: string
      features: string[]
    }[]
  }
  disabledTooltipContent?: string
}
const findPlanById = (plans: IPlan[], id: string | undefined) => plans.find((plan) => plan?.product?.id === id)

const getPlanPrice = (plan: IPlan | undefined) => {
  if (!plan) return NaN
  const price = plan.product.defaultPrice.unitAmount as number
  return price
}

const displayPrice = (price: number | undefined) => {
  if (price === undefined) {
    return ''
  }
  return `${price}`
}

const displayPriceFormatted = (price: number | undefined, digits?: number) => {
  if (price === undefined) {
    return ''
  }
  return `$${(price / 100).toFixed(digits)}`
}

const planNameDisplay = (plan: IPlan | undefined) => {
  if (!plan) return ''
  /* split to not display monthly vs 12 month here*/
  return plan?.product?.name.split?.(' ')?.[0]
}

const formatCurrency = (num: number) => {
  return num.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  })
}

const formatNumber = (num: number) => {
  return num.toLocaleString('en-US')
}

const getCommitmentVolumeForProduct = (id: string, data: Product[]) => {
  const price = getDefaultPrice(id, data)
  if (price?.tiers) {
    return price.tiers[0].upTo
  }
  return 0
}

const getProduct = (id: string | undefined, data: Product[]) => {
  return data.find((product) => product.id === id)
}

const getDefaultPrice = (id: string | undefined, data: Product[]) => {
  const product = getProduct(id, data)
  return product?.defaultPrice
}

const getUnitPriceForProduct = (id: string, data: Product[]) => {
  const price = getDefaultPrice(id, data)
  if (price?.tiers) {
    return price.tiers[1].unitAmount
  }
  return price?.unitAmount
}

const getIntervalAdverb = (interval: string | undefined) => {
  if (interval === 'month') return 'monthly'
  return 'annual'
}

export {
  displayPrice,
  displayPriceFormatted,
  filterProductsByMetadata,
  findDefaultProduct,
  findPlanById,
  findProductByMetadata,
  formatCurrency,
  formatNumber,
  getCommitmentVolumeForProduct,
  getDefaultPrice,
  getPlanPrice,
  getProduct,
  getUnitPriceForProduct,
  planNameDisplay,
  getIntervalAdverb,
}

export { Interval, PlanType, ProductType, UsageType }

export type { IPlan, Features }
